import AccountSwitcherSheet from "@/src/components/account/account-switcher";
import ConfirmModal from "@/src/components/action-modals/confirm-modal";
import { default as ScButton } from "@/src/components/ui-kit/button";
import DropDown, { ContextMenuItem } from "@/src/components/ui-kit/drop-down";
import { NotificationBadge } from "@/src/components/ui-kit/notification-badge";
import UserAvatar from "@/src/components/ui-kit/user-avatar";
import { useUnreadNotificationCount } from "@/src/services/api/notification/notification.hooks";
import { useAuth } from "@/src/contexts/auth.context";
import {
  getRefreshToken,
  getToken,
  useLogoutMutation,
} from "@/src/services/api/auth.api";
import { OrgFeatureKey } from "@/src/constants/org-features";
import { useOrgFeatures } from "@/src/services/organization/org-features.hooks";
import { useGetOrganizationConfigQuery } from "@/src/services/organization/organization.hooks";
import { useTheme } from "@/src/styles/theme-provider";
import { usePathname, useRouter } from "expo-router";
import {
  ArchiveBook,
  ArrowLeft2,
  ArrowRight2,
  ArrowSwapVertical,
  Book1,
  Box,
  Calendar,
  Element,
  LogoutCurve,
  MessageText,
  Notification,
  People,
  Profile2User,
  Setting3,
  Star1,
  TableDocument,
} from "iconsax-react-nativejs";
import React, { ReactElement, useEffect, useRef, useState } from "react";
import { firstUsableImageUrl } from "@/src/utils/image-url.utils";
import { useTranslation } from "react-i18next";
import { formatRoleLabel } from "@/src/utils/role.utils";
import { formatUserName } from "@/src/utils/string.utils";
import {
  Image,
  Pressable,
  Text,
  useWindowDimensions,
  View,
} from "react-native";
import OrgSwitcherSheet from "../../account/organization-switch";
import { HoverTooltip } from "../hover-tooltip";

type NavItem = Readonly<{
  path: string;
  translationKey: string;
  icon: React.ReactElement;
  onPress?: () => void;
  adminOnly?: boolean;
  /** Hidden until the org admin switches this feature on in Settings. */
  feature?: OrgFeatureKey;
  /** Unread count. Zero or undefined renders no badge. */
  badgeCount?: number;
}>;

type NavBarItemProps = Readonly<{
  item: NavItem;
  isAdmin: boolean;
  isActive: boolean;
  collapsed: boolean;
}>;

const NavBarItem = ({
  item,
  isAdmin,
  isActive,
  collapsed,
}: NavBarItemProps) => {
  const { themeVars } = useTheme();

  if (item?.adminOnly && !isAdmin) return null;

  if (collapsed) {
    return (
      <View
        className={`flex-row items-center justify-center ${!isActive ? "hover:bg-primary-2 hover:rounded-xl" : ""}`}
      >
        <Pressable
          onPress={item?.onPress}
          className={`p-3 rounded-xl hover:bg-ash-3 ${isActive ? "bg-primary-3" : ""}`}
        >
          {/* relative wrapper so the badge pins to the icon, not the padded
              Pressable — otherwise it floats away from the glyph. */}
          <View className="relative">
            {React.cloneElement(
              item.icon as React.ReactElement<{ color: string; size: number }>,
              {
                color: isActive
                  ? (themeVars["--color-primary-default"] as string)
                  : (themeVars["--color-slate-default"] as string),
                size: 22,
              },
            )}
            <NotificationBadge count={item?.badgeCount} variant="overlay" />
          </View>
        </Pressable>
      </View>
    );
  }

  return (
    <View
      className={`flex-row items-center ${!isActive ? "hover:bg-primary-2 hover:rounded-xl" : ""}`}
    >
      <ScButton
        variant="nav"
        text={item?.translationKey}
        icon={item?.icon as ReactElement<any>}
        active={isActive}
        onPress={item?.onPress}
        badge={<NotificationBadge count={item?.badgeCount} variant="inline" />}
      />
    </View>
  );
};

type NavBarUserProfileProps = Readonly<{
  firstName?: string;
  lastName?: string;
  profileImageUrl?: string;
  itemId?: string;
  displayName: string;
  userRoleLabel: string;
  profileMenuItems: readonly ContextMenuItem[];
  onNavigateToProfile: () => void;
  collapsed: boolean;
}>;

const NavBarUserProfile = ({
  firstName,
  lastName,
  profileImageUrl,
  displayName,
  userRoleLabel,
  profileMenuItems,
  onNavigateToProfile,
  collapsed,
}: NavBarUserProfileProps) => {
  const { themeVars } = useTheme();
  const [isNameHovered, setIsNameHovered] = useState(false);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // A blank string is "no picture", not a URL — see firstUsableImageUrl.
  const resolvedUri = firstUsableImageUrl(profileImageUrl);
  const [avatarFailed, setAvatarFailed] = useState(false);
  useEffect(() => {
    setAvatarFailed(false);
  }, [resolvedUri]);
  const avatarUri = avatarFailed ? null : resolvedUri;

  const handleMouseEnter = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      setIsNameHovered(true);
    }, 400);
  };

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setIsNameHovered(false);
  };

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    };
  }, []);

  return (
    <View className="flex-row gap-1 items-center justify-center py-2 px-1 hover:bg-primary-1 hover:rounded transition-opacity">
      <Pressable
        onPress={onNavigateToProfile}
        className="flex-row gap-1 items-center flex-1 justify-center min-w-0"
      >
        <View className="rounded-full overflow-hidden">
          {avatarUri ? (
            <Image
              source={{ uri: avatarUri }}
              style={{ width: 35, height: 35 }}
              resizeMode="cover"
              // A stored picture that has gone away left an empty circle here,
              // because the initials branch is only reached when there is no URL
              // at all. Treat a failed load as "no picture".
              onError={() => setAvatarFailed(true)}
            />
          ) : (
            <UserAvatar firstName={firstName} lastName={lastName} size={35} />
          )}
        </View>
        {!collapsed && (
          <View className="flex-col ml-1 flex-1 min-w-0">
            <HoverTooltip
              className="min-w-0"
              content={
                <Text
                  className="text-chalk text-xs"
                  style={{ width: 160, flexWrap: "wrap" }}
                >
                  {displayName}
                </Text>
              }
            >
              <Text
                className="font-semibold text-onyx"
                numberOfLines={1}
                ellipsizeMode="tail"
              >
                {displayName}
              </Text>
            </HoverTooltip>
            <Text className="text-slate" numberOfLines={1} ellipsizeMode="tail">
              {userRoleLabel}
            </Text>
          </View>
        )}
      </Pressable>

      {!collapsed && (
        <DropDown
          items={[...profileMenuItems]}
          menuWidth={200}
          trigger={
            <ArrowSwapVertical
              size={20}
              color={themeVars["--color-slate-default"] as string}
            />
          }
        />
      )}
    </View>
  );
};

const NavBar = () => {
  const { t } = useTranslation();
  const { themeVars } = useTheme();
  const { user, appRole, isAdmin, logout } = useAuth();
  const pathname = usePathname();
  const router = useRouter();
  const { width: windowWidth } = useWindowDimensions();
  const isLargeScreen = windowWidth >= 1024;

  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showAccountSwitcher, setShowAccountSwitcher] = useState(false);
  const [showOrgSwitcher, setShowOrgSwitcher] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const isCollapsed = !isLargeScreen || collapsed;

  const { mutateAsync: logoutMutateAsync } = useLogoutMutation();
  const { data: orgConfig } = useGetOrganizationConfigQuery();
  const { isEnabled: isFeatureEnabled } = useOrgFeatures();
  const unreadCount = useUnreadNotificationCount(!!user);

  const displayName =
    (user && formatUserName(user.firstName, user.lastName)) ||
    t("LABEL_UNKNOWN_USER");

  // `formatRoleLabel` splits on `_` as well as `-`: the prod tenant's slug is
  // `super_admin`, which the old `-`-only split rendered as "Super_admin".
  const userRoleLabel = appRole
    ? formatRoleLabel(appRole)
    : t("LABEL_UNKNOWN_ROLE");

  const mainNavItems: readonly NavItem[] = [
    {
      path: "/feeds",
      translationKey: "BTN_FEED",
      icon: <TableDocument />,
      onPress: () => router.push("/feeds"),
    },
    {
      path: "/news",
      translationKey: "BTN_NEWS",
      feature: "news",
      icon: <ArchiveBook />,
      onPress: () => router.push("/news/latest-news"),
    },
    {
      path: "/events",
      translationKey: "BTN_EVENTS",
      feature: "events",
      icon: <Calendar />,
      onPress: () => router.push("/events"),
    },
    {
      path: "/groups",
      translationKey: "BTN_GROUPS",
      icon: <People />,
      onPress: () => router.push("/groups"),
    },
    {
      path: "/uam",
      translationKey: "BTN_USERS",
      icon: <Profile2User />,
      onPress: () => router.push("/uam"),
      adminOnly: true,
    },
    {
      path: "/wiki",
      translationKey: "BTN_WIKI",
      feature: "wiki",
      icon: <Book1 />,
      onPress: () => router.push("/(tabs)/wiki/wiki-articles"),
    },
    {
      path: "/wiki-feedback",
      translationKey: "BTN_FEEDBACK",
      feature: "feedback",
      icon: <MessageText />,
      onPress: () => router.push("/wiki-feedback"),
    },
    {
      path: "/feature-requests",
      translationKey: "BTN_FEATURE_REQUESTS",
      feature: "featureRequests",
      icon: <Box />,
      onPress: () => router.push("/feature-requests"),
    },
  ];

  const bottomNavItems: readonly NavItem[] = [
    {
      path: "/dashboard",
      translationKey: "BTN_DASHBOARD",
      icon: <Element />,
      onPress: () => router.push("/dashboard"),
      adminOnly: true,
    },
    // Rewardly is hidden until the feature ships — the screens exist and are
    // reachable by URL, they are just not advertised yet. Restore this entry
    // rather than rebuilding it.
    // {
    //   path: "/rewardly",
    //   translationKey: "BTN_REWARDLY",
    //   icon: <Star1 />,
    //   onPress: () => router.push("/rewardly"),
    // },
    {
      path: "/notification",
      translationKey: "BTN_NOTIFICATION",
      onPress: () => router.push("/notification"),
      icon: <Notification />,
      badgeCount: unreadCount,
    },
    {
      path: "/settings",
      translationKey: "BTN_SETTINGS",
      icon: <Setting3 />,
      onPress: () => router.push("/settings"),
    },
  ];

  const profileMenuItems: readonly ContextMenuItem[] = [
    {
      label: t("BTN_SWITCH_CLUB"),
      onPress: () => setShowOrgSwitcher(true),
      rightIcon: (
        <ArrowSwapVertical
          size={16}
          color={themeVars["--color-slate-default"] as string}
        />
      ),
    },
    {
      label: t("BTN_SWITCH_ACCOUNT"),
      onPress: () => setShowAccountSwitcher(true),
      rightIcon: (
        <ArrowSwapVertical
          size={16}
          color={themeVars["--color-slate-default"] as string}
        />
      ),
    },
    {
      label: t("BTN_LOGOUT"),
      onPress: () => setShowLogoutModal(true),
      destructive: true,
      rightIcon: (
        <LogoutCurve
          size={18}
          color={themeVars["--color-crimson-default"] as string}
        />
      ),
    },
  ];

  const isActive = (path: string) => {
    if (path === "/") return pathname === "/";
    return pathname === path || pathname?.startsWith(`${path}/`);
  };

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      const refreshToken = await getRefreshToken();
      const accessToken = await getToken();
      if (refreshToken && accessToken) {
        await logoutMutateAsync({ refreshToken, accessToken });
      }
    } catch {
    } finally {
      await logout();
      setIsLoggingOut(false);
    }
  };

  const handleConfirmLogout = async () => {
    setShowLogoutModal(false);
    await handleLogout();
  };

  return (
    <View
      className="flex-col bg-chalk border border-ash-3 rounded-2xl p-4 justify-between items-center"
      style={{ width: isCollapsed ? 64 : 228 }}
    >
      <View className="pb-3 flex-col gap-4 w-full">
        {/* Logo + collapse toggle */}
        <View className="pb-3 flex-col gap-4 w-full">
          {/* Logo + collapse toggle */}
          <View className="flex-row pb-4 w-full justify-between items-center border-ash-3 gap-2 border-b">
            {!isCollapsed && (
              <View className="flex-row flex-1 justify-center items-center p-1 border rounded-lg border-ash-3">
                <Image
                  resizeMode="contain"
                  style={{ width: 100, height: 35 }}
                  source={require("@/src/assets/images/selise-full.svg")}
                />
                <Text className="text-primary font-semibold ml-1">{t("LABEL_SELISE_CLUB")}</Text>
              </View>
            )}
            {isLargeScreen && (
              <Pressable
                onPress={() => setCollapsed((prev) => !prev)}
                className={`p-1.5 rounded-lg hover:bg-ash-3 ${isCollapsed ? "mx-auto" : ""}`}
              >
                {collapsed ? (
                  <ArrowRight2 size={16} color={themeVars["--color-slate-default"] as string} />
                ) : (
                  <ArrowLeft2 size={16} color={themeVars["--color-slate-default"] as string} />
                )}
              </Pressable>
            )}
          </View>
        </View>
        {/* Main nav */}
        <View className="w-full gap-1">
          {mainNavItems
            .filter((navItem) => !navItem.feature || isFeatureEnabled(navItem.feature))
            .map((navItem) => (
            <NavBarItem
              key={navItem?.path}
              item={navItem}
              isAdmin={isAdmin}
              isActive={isActive(navItem?.path)}
              collapsed={isCollapsed}
            />
          ))}
        </View>
      </View>

      <View className="w-full">
        <View className="flex-col justify-start border-ash-3 py-2 border-t gap-1">
          {bottomNavItems.map((navItem) => {
            return (
              <NavBarItem
                key={navItem?.path}
                item={navItem}
                isAdmin={isAdmin}
                isActive={isActive(navItem?.path)}
                collapsed={isCollapsed}
              />
            );
          })}
        </View>

        <View className="gap-1 border-t border-ash-3 pt-4 pb-2">
          <NavBarUserProfile
            firstName={user?.firstName ?? undefined}
            lastName={user?.lastName ?? undefined}
            profileImageUrl={user?.profileImageUrl ?? undefined}
            itemId={user?.itemId}
            displayName={displayName}
            userRoleLabel={userRoleLabel}
            profileMenuItems={profileMenuItems}
            onNavigateToProfile={() => router.push(`/profile/${user?.itemId}`)}
            collapsed={isCollapsed}
          />
        </View>
      </View>

      <ConfirmModal
        visible={showLogoutModal}
        title="LABEL_LOGOUT_CONFIRM"
        description="HINT_LOGOUT_CONFIRM"
        confirmText={t("BTN_LOGOUT")}
        cancelText={t("BTN_CANCEL")}
        loading={isLoggingOut}
        onCancel={() => setShowLogoutModal(false)}
        onConfirm={handleConfirmLogout}
      />

      <AccountSwitcherSheet
        visible={showAccountSwitcher}
        onClose={() => setShowAccountSwitcher(false)}
      />

      <OrgSwitcherSheet
        visible={showOrgSwitcher}
        onClose={() => setShowOrgSwitcher(false)}
      />
    </View>
  );
};

export default NavBar;
